import React from 'react';
import { ArrowRight, Check, CircleDot, Database, Map, Settings2, Users } from 'lucide-react';
import Alt4Layout from '../components/Alt4Layout';
import { ClosingCta, PrimaryCta, Reveal, SectionIntro } from '../components/Alt4Shared';
import { MIGRATION_STEPS } from '../data/site';

const readiness = [
  { icon: Database, title: 'Data readiness', body: 'Prepare and validate the records that carry the operation forward.' },
  { icon: Settings2, title: 'Workflow readiness', body: 'Configure rules, permissions, channels, and exception paths.' },
  { icon: Users, title: 'Team readiness', body: 'Align roles, review points, and launch responsibilities by location.' },
];

export default function Alt4Migration() {
  return (
    <Alt4Layout title="Migration">
        <section className="alt4-page-hero alt4-section">
          <div className="alt4-container alt4-hero-grid alt4-page-hero__grid">
            <Reveal className="alt4-hero-copy">
              <span className="alt4-eyebrow">A transition built around operations</span>
              <h1>Move the operation, not just the records.</h1>
              <p>A successful transition aligns records, communications, workflows, permissions, and team readiness—so the AI-native PMS is ready to run from day one.</p>
              <PrimaryCta />
            </Reveal>
            <Reveal className="alt4-panel alt4-migration-readiness" delay={0.08}>
              <header><span className="alt4-interface-label">Readiness view</span><span className="alt4-status alt4-status--running"><CircleDot aria-hidden="true" /> In progress</span></header>
              {readiness.map(({ icon: Icon, title }, index) => (
                <div key={title}>
                  <span className={index === 0 ? 'is-done' : index === 1 ? 'is-active' : ''}>{index === 0 ? <Check aria-hidden="true" /> : <Icon aria-hidden="true" />}</span>
                  <strong>{title}</strong>
                  <small>{index === 0 ? 'Validated' : index === 1 ? 'Calibrating' : 'Up next'}</small>
                </div>
              ))}
              <footer><Map aria-hidden="true" /><span><strong>Location-aware rollout</strong><small>Each office advances through shared readiness gates.</small></span></footer>
            </Reveal>
          </div>
        </section>

        <section className="alt4-section alt4-section--pale">
          <div className="alt4-container">
            <SectionIntro
              eyebrow="A staged operating transition"
              title="Prepare the system and the organization together."
              body="Discovery, preparation, calibration, validation, rollout, and ongoing tuning move through clear readiness gates. Each stage prepares the operation for what comes next."
            />
            <ol className="alt4-migration-steps">
              {MIGRATION_STEPS.map((step, index) => (
                <li className="alt4-migration-step" key={step.title}>
                  <Reveal delay={index * 0.04}>
                    <span className="alt4-migration-step__number">{step.number}</span>
                    <div><h3>{step.title}</h3><p>{step.description}</p></div>
                    {index < MIGRATION_STEPS.length - 1 && <ArrowRight aria-hidden="true" />}
                  </Reveal>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="alt4-section">
          <div className="alt4-container">
            <SectionIntro
              eyebrow="Readiness before rollout"
              title="Calibrate how the office runs, not just where the data lives."
              body="Records are one part of the transition. Operational readiness also covers communications, permission scopes, workflows, review thresholds, and team ownership."
            />
            <div className="alt4-grid alt4-grid--three alt4-readiness-grid">
              {readiness.map(({ icon: Icon, title, body }, index) => (
                <Reveal className="alt4-card alt4-readiness-card" delay={index * 0.06} key={title}>
                  <Icon aria-hidden="true" />
                  <h3>{title}</h3>
                  <p>{body}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="alt4-section alt4-section--blue">
          <div className="alt4-container alt4-grid alt4-grid--two alt4-rollout-section">
            <SectionIntro
              inverse
              eyebrow="Location-aware rollout"
              title="Advance each location when its operation is ready."
              body="The rollout can follow the structure of the group. Readiness checks, validation, and support stay connected at every stage, with tuning continuing after each launch."
            />
            <div className="alt4-panel alt4-panel--blue alt4-rollout-map">
              <span className="alt4-interface-label">Rollout map</span>
              <div><span><Check aria-hidden="true" /></span><p><strong>Foundation</strong>Shared operating model validated</p></div>
              <div><span><CircleDot aria-hidden="true" /></span><p><strong>Location wave</strong>Configuration and team readiness checked</p></div>
              <div><span><Settings2 aria-hidden="true" /></span><p><strong>Live tuning</strong>Workflow behavior reviewed and calibrated</p></div>
            </div>
          </div>
        </section>

        <ClosingCta
          eyebrow="Plan the transition"
          title="Build a migration around operational readiness."
          body="Talk through your records, workflows, location structure, permissions, and rollout approach."
        />
    </Alt4Layout>
  );
}
